import type { CoreActivity, CoreReference } from "./core-stream";

/** Only user-visible content belongs in history. Provider reasoning/tool payloads do not. */
export type CoreConversationMessage = {
  id: string; role: "user" | "assistant"; text: string; meta?: string;
  references?: CoreReference[]; pending?: boolean; error?: boolean; failure?: string;
  stopped?: boolean; startedAt?: number; completedAt?: number;
  activities?: CoreActivity[]; summaries?: Record<string, string>;
};
export type CoreMessage = CoreConversationMessage;
export type CoreConversation = {
  id: string; title: string; createdAt: string; updatedAt: string; version: number;
  messages: CoreConversationMessage[]; activeTurnId?: string;
};
export type CoreConversationSummary = {
  id: string; title: string; createdAt: string; updatedAt: string; messageCount: number;
  preview: string; status: "empty" | "running" | "complete" | "stopped" | "error";
};
export type CoreConversationList = { conversations: CoreConversationSummary[]; nextCursor: string | null };
export const CONVERSATION_MAX_BYTES = 700_000;
export const CONVERSATION_MAX_MESSAGES = 200;
export const CONVERSATION_LEASE_MS = 90_000;
export const conversationIdValid = (value: unknown): value is string => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
export function conversationTitle(value: unknown) {
  if (typeof value !== "string" || !value.trim() || value.trim().length > 120) throw new ConversationError("Use a title between 1 and 120 characters.", 400, "INVALID_TITLE");
  return value.trim().replace(/\s+/g, " ");
}
export class ConversationError extends Error {
  constructor(message: string, public status = 409, public code = "CONVERSATION_CONFLICT", public conversation?: CoreConversation) { super(message); this.name = "ConversationError"; }
}
export function conversationPreview(text: string, references: CoreReference[] = []) {
  return text
    .replace(/\[(source|signal|claim|system|simulation|projection|portfolio|world-model):([^\]]+)\]/g, (_match, kind: string, id: string) => references.find(reference => reference.kind === kind && reference.id === id)?.title || "")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s*```[^\n]*$/gm, "")
    .replace(/^\s{0,3}(?:#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/gm, "")
    .replace(/(\*{1,2}|_{1,2}|~~)(.*?)\1/g, "$2")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ").trim().slice(0, 180);
}
export function summarizeConversation(doc: CoreConversation): CoreConversationSummary {
  const last = doc.messages[doc.messages.length - 1];
  return { id: doc.id, title: doc.title, createdAt: doc.createdAt, updatedAt: doc.updatedAt, messageCount: doc.messages.length,
    preview: conversationPreview(last?.text || doc.messages.findLast(m => m.role === "user")?.text || "", last?.references),
    status: doc.activeTurnId ? "running" : last?.stopped ? "stopped" : last?.error ? "error" : last ? "complete" : "empty" };
}
export function conversationTranscript(doc: CoreConversation, beforeTurnId?: string) {
  const transcript: Array<{ role: "user" | "assistant"; content: string }> = [];
  for (const message of doc.messages) {
    if (message.id === `${beforeTurnId}-user`) break;
    // Attachments are not retained as hidden blobs. Their readable description survives.
    const content = message.text + (message.meta && message.role === "user" ? `\n[Attachment: ${message.meta}; original file is not retained in conversation history.]` : "")
      + (message.text && message.role === "assistant" && (message.stopped || message.error) ? "\n[This response was interrupted before completion.]" : "");
    if (content.trim()) transcript.push({ role: message.role, content });
  }
  if (JSON.stringify(transcript).length > 160_000) throw new ConversationError("This conversation has reached its context limit. Start a new conversation to continue; your history is saved.", 413, "CONTEXT_LIMIT");
  return transcript;
}
